import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { PaymentClient, type PaymentClientEnum } from "@fitness-app/data-models/entities/Product";
import { type ProductsSettings } from "@fitness-app/data-models/entities/ProductsSettings";

import { useAppSelector } from "~/store/initializeStore";

export function getActivePaymentGatewaysForAccount(integration: ProductsSettings) {
  const enabledPaymentGateways: PaymentClientEnum[] = [];

  if (integration.isStripeConnected) {
    enabledPaymentGateways.push(PaymentClient.STRIPE);
  }

  if (integration.tpay && integration.isTPayConnected) {
    enabledPaymentGateways.push(PaymentClient.TPAY);
  }

  return enabledPaymentGateways;
}

export const usePaymentGateways = () => {
  const { t } = useTranslation(["products"]);
  const integration = useAppSelector((store) => store.productsSettings.data);
  const enabledPaymentGateways = useAppSelector((store) => store.product.details?.enabledPaymentGateways) || [];
  const paymentMethodsConfigurationForProduct = useAppSelector((store) => store.product.details?.paymentsMethods) || {};

  const activePaymentGateways = useMemo(() => {
    if (!integration) {
      return [];
    }

    return getActivePaymentGatewaysForAccount(integration);
  }, [integration]);

  const activePaymentGatewaysForProduct = enabledPaymentGateways.filter((gateway) =>
    activePaymentGateways.includes(gateway),
  );

  const activePaymentGatewaysOptions = useMemo(() => {
    return activePaymentGateways.map((gateway) => ({
      label: t(`productTransactions.paymentGateway.${gateway.toLowerCase()}`),
      value: gateway,
    }));
  }, [activePaymentGateways]);

  return {
    activePaymentGateways,
    activePaymentGatewaysOptions,
    activePaymentGatewaysForProduct,
    paymentMethodsConfigurationForProduct,
    isStripeEnabled: activePaymentGatewaysForProduct.includes(PaymentClient.STRIPE),
    isTpayEnabled: activePaymentGatewaysForProduct.includes(PaymentClient.TPAY),
    isTpayConnected: activePaymentGateways.includes(PaymentClient.TPAY),
    isStripeConnected: activePaymentGateways.includes(PaymentClient.STRIPE),
  };
};
